/**
 * Visibility Engine - Shared logic for conditional control visibility
 *
 * Uses topological sort for O(n + m) performance
 * - n = number of controls
 * - m = number of dependency edges
 * Used by:
 * - Client: useControlVisibility hook
 * - Server: answerBundleControlCount/AnswerCount
 */

// Types matching UserJourney schema
export type AnswerOptionOperator = 'AND' | 'OR';

export interface DependencyInput {
  controlIdentifier: string;
  answerOptionIdentifiers: string[];
  isInverse: boolean;
  // How answerOptionIdentifiers combine: every option selected, or any one. Absent means AND.
  answerOptionOperator?: AnswerOptionOperator | null;
}

export interface Dependency {
  dependentControlIdentifier: string;
  inputs: DependencyInput[];
}

// Answer map: controlIdentifier -> Set of selected answerOptionIdentifiers
export type AnswerMap = Map<string, Set<string>>;

/**
 * Build answer map from answers array
 * @param answers - Array of { controlIdentifier, values: [{ answerOptionIdentifier }] }
 */
export function buildAnswerMap(
  answers:
    | {
        controlIdentifier: string;
        values?: { answerOptionIdentifier?: string | null }[] | null;
      }[]
    | null
    | undefined
): AnswerMap {
  const map = new Map<string, Set<string>>();

  if (!answers) {
    return map;
  }

  for (const answer of answers) {
    if (!answer?.controlIdentifier) {
      continue;
    }

    const identifiers = new Set<string>();
    if (answer.values) {
      for (const value of answer.values) {
        if (value?.answerOptionIdentifier) {
          identifiers.add(value.answerOptionIdentifier);
        }
      }
    }
    map.set(answer.controlIdentifier, identifiers);
  }

  return map;
}

/**
 * Evaluate a single condition (one parent control)
 * @returns true if condition is MET (control should be visible)
 */
function evaluateSingleCondition(
  input: DependencyInput,
  answerMap: AnswerMap
): boolean {
  const selectedOptions = answerMap.get(input.controlIdentifier);

  // If parent control has no answers, condition is NOT met
  // isInverse=false: "show if selected" → not selected → hidden
  // isInverse=true: "show if NOT selected" → not selected → visible
  if (!selectedOptions || selectedOptions.size === 0) {
    return input.isInverse;
  }

  // Empty answerOptionIdentifiers = no specific answer required = always pass
  if (
    !input.answerOptionIdentifiers ||
    input.answerOptionIdentifiers.length === 0
  ) {
    return !input.isInverse; // Has some answer, no specific required
  }

  const matched =
    input.answerOptionOperator === 'OR'
      ? input.answerOptionIdentifiers.some((id) => selectedOptions.has(id))
      : input.answerOptionIdentifiers.every((id) => selectedOptions.has(id));

  return input.isInverse ? !matched : matched;
}

/**
 * Evaluate one dependency rule: AND across its inputs.
 * @returns true if ALL conditions pass (visible), false if ANY fails (hidden)
 */
function evaluateAllConditions(
  inputs: DependencyInput[],
  answerMap: AnswerMap,
  visibility: Map<string, boolean>
): boolean {
  // Empty inputs array = no conditions = visible (fail-open)
  if (!inputs || inputs.length === 0) {
    return true;
  }

  for (const input of inputs) {
    if (!input?.controlIdentifier) {
      continue;
    }

    const parentId = input.controlIdentifier;
    const parentVisible = visibility.get(parentId);

    // If parent visibility unknown, assume visible (fail-open)
    // This handles case where parent is not in bundle
    if (parentVisible === undefined) {
      continue;
    }

    // If parent is hidden, dependent is also hidden
    if (!parentVisible) {
      return false;
    }

    // Parent is visible - evaluate the condition
    const conditionMet = evaluateSingleCondition(input, answerMap);
    if (!conditionMet) {
      return false; // AND logic: any failure = hidden
    }
  }

  // All conditions passed
  return true;
}

/**
 * Evaluate every rule targeting one control: OR across rules.
 *
 * Several rules may name the same dependentControlIdentifier. Each is an
 * alternative way to reveal the control, which is the only way to express
 * "show B when A is x OR y" for answer types that hold a single option.
 */
function evaluateAnyRule(
  rules: Dependency[],
  answerMap: AnswerMap,
  visibility: Map<string, boolean>
): boolean {
  if (rules.length === 0) {
    return true;
  }
  return rules.some((rule) =>
    evaluateAllConditions(rule.inputs, answerMap, visibility)
  );
}

/**
 * Build dependency graph for topological sort
 * Returns:
 * - depMap: controlId -> every Dependency rule targeting it
 * - inDegree: controlId -> number of unresolved parents
 * - children: parentId -> list of dependent controlIds
 */
function buildDependencyGraph(
  dependencies: Dependency[],
  controlIdSet: Set<string>
): {
  depMap: Map<string, Dependency[]>;
  inDegree: Map<string, number>;
  children: Map<string, string[]>;
} {
  const depMap = new Map<string, Dependency[]>();
  const inDegree = new Map<string, number>();
  const children = new Map<string, string[]>();

  // Initialize all controls with 0 in-degree
  for (const controlId of controlIdSet) {
    inDegree.set(controlId, 0);
    children.set(controlId, []);
  }

  // Parents are collected across every rule for a child, so all resolve before it.
  const parentsByChild = new Map<string, Set<string>>();

  for (const dep of dependencies) {
    if (!dep?.dependentControlIdentifier) {
      continue;
    }

    const childId = dep.dependentControlIdentifier;
    if (!controlIdSet.has(childId)) {
      continue;
    }

    const rules = depMap.get(childId) ?? [];
    rules.push(dep);
    depMap.set(childId, rules);

    const parentIds = parentsByChild.get(childId) ?? new Set<string>();
    for (const input of dep.inputs ?? []) {
      if (
        input?.controlIdentifier &&
        controlIdSet.has(input.controlIdentifier)
      ) {
        parentIds.add(input.controlIdentifier);
      }
    }
    parentsByChild.set(childId, parentIds);
  }

  // One edge per parent-child pair; a duplicate would over-decrement the in-degree.
  for (const [childId, parentIds] of parentsByChild) {
    inDegree.set(childId, parentIds.size);
    for (const parentId of parentIds) {
      const parentChildren = children.get(parentId) ?? [];
      parentChildren.push(childId);
      children.set(parentId, parentChildren);
    }
  }

  return { depMap, inDegree, children };
}

/**
 * Evaluate visibility for all controls using TOPOLOGICAL SORT
 *
 * Algorithm (O(n + m)):
 * 1. Build dependency graph
 * 2. Find all "root" controls (in-degree = 0) - these are always visible
 * 3. Process controls in topological order using a queue
 * 4. Each control is processed exactly ONCE
 *
 * @param dependencies - Array of dependency rules
 * @param answerMap - Map of controlIdentifier -> selected answerOptionIdentifiers
 * @param controlIds - List of all control identifiers in the bundle
 * @returns Map of controlIdentifier -> isVisible
 */
export function evaluateAllVisibility(
  dependencies: Dependency[] | null | undefined,
  answerMap: AnswerMap | null | undefined,
  controlIds: string[] | null | undefined
): Map<string, boolean> {
  const visibility = new Map<string, boolean>();

  // Input validation
  if (!controlIds || controlIds.length === 0) {
    return visibility;
  }

  if (!answerMap) {
    answerMap = new Map();
  }

  if (!dependencies) {
    dependencies = [];
  }

  const controlIdSet = new Set(controlIds);

  // Build dependency graph - O(m)
  const { depMap, inDegree, children } = buildDependencyGraph(
    dependencies,
    controlIdSet
  );

  // Initialize queue with "root" controls (no dependencies) - O(n)
  const queue: string[] = [];
  for (const controlId of controlIds) {
    const degree = inDegree.get(controlId) ?? 0;
    if (degree === 0) {
      queue.push(controlId);
      // Root controls with no dependencies are visible
      if (!depMap.has(controlId)) {
        visibility.set(controlId, true);
      }
    }
  }

  // Process queue - each control processed exactly once - O(n + m)
  let head = 0;
  while (head < queue.length) {
    const controlId = queue[head++];

    // If not yet resolved, evaluate now
    if (!visibility.has(controlId)) {
      const rules = depMap.get(controlId);
      if (!rules || rules.length === 0) {
        // No dependency rule = visible (fail-open)
        visibility.set(controlId, true);
      } else {
        // Evaluate rules - all parents are already resolved
        visibility.set(
          controlId,
          evaluateAnyRule(rules, answerMap, visibility)
        );
      }
    }

    // Decrement in-degree of children and add to queue if ready
    const childList = children.get(controlId) ?? [];
    for (const childId of childList) {
      const currentDegree = inDegree.get(childId) ?? 0;
      const newDegree = currentDegree - 1;
      inDegree.set(childId, newDegree);

      if (newDegree === 0) {
        queue.push(childId);
      }
    }
  }

  // Handle cycles: any unprocessed controls are made visible (fail-open)
  for (const controlId of controlIds) {
    if (!visibility.has(controlId)) {
      console.warn(
        `Visibility cycle detected for control: ${controlId}, defaulting to visible`
      );
      visibility.set(controlId, true);
    }
  }

  return visibility;
}

/**
 * Count visible controls
 */
export function countVisibleControls(visibility: Map<string, boolean>): number {
  let count = 0;
  for (const isVisible of visibility.values()) {
    if (isVisible) {
      count++;
    }
  }
  return count;
}
